/**
 * Кто остановил запись голоса (12.09.2026, договор `record-integrity.md` §2.1).
 *
 * ⚠️ ГЛАВНОЕ ПРАВИЛО: `onstop` БЕЗ НАШЕЙ ОТМЕТКИ — ЭТО САМООСТАНОВКА. Разбор
 * 12.09.2026 показал три пути, на которых речь теряется беззвучно: дорожка
 * микрофона кончилась и Chromium сам остановил рекордер; дорожка замолчала,
 * потому что микрофон забрало другое приложение; рекордер упал. Во всех трёх
 * случаях `onstop` выглядит в точности как нажатие кнопки, блок молча переходит
 * в «Отправить», и ученик отправляет обрывок, не поняв, что произошло.
 *
 * ⚠️ СТАРШИНСТВО: БЕДА ВАЖНЕЕ КНОПКИ. Ученик, у которого микрофон отвалился на
 * сороковой секунде, всё равно в конце нажмёт «Остановить» — и если считать
 * причиной кнопку, потеря снова станет невидимой.
 *
 * `reload` — запись собрана из черновика после перезагрузки страницы: чем она
 * кончилась на самом деле, мы не знаем, и выдавать это за нажатие кнопки нельзя.
 *
 * Причина идёт на сервер полем `stop_reason` и ложится в `progress.audio_answer_durations`,
 * чтобы следующий разбор не начинался с нуля.
 */

/**
 * `stop` — кнопка; `pause` — кнопка, нажатая с паузы; `limit` — автостоп на
 * пределе задания; `leave` — уход со страницы; `mute`, `ended`, `error` — потеря
 * микрофона; `self` — рекордер остановился сам, и почему — неизвестно.
 *
 * Договор принимает любую строку `^[a-z_]{1,16}$` и сохраняет как есть, поэтому
 * `limit`, `leave` и `self` не ломают сервер, хотя в его таблице их нет.
 */
export type StopReason = 'stop' | 'pause' | 'limit' | 'leave' | 'mute' | 'ended' | 'error' | 'self' | 'reload';

export type StopFlags = {
  /** Нажата «Остановить» — в задании или в полоске вверху экрана. */
  button?: boolean;
  /** Нажата с паузы. */
  paused?: boolean;
  /** Автостоп на пределе задания. */
  limit?: boolean;
  /** Блок сняли с экрана или ушли со страницы. */
  leave?: boolean;
  /** `track.onended` — дорожки больше нет. */
  ended?: boolean;
  /** `track.onmute` — дорожка жива, но молчит. */
  mute?: boolean;
  /** `rec.onerror`. */
  error?: boolean;
};

export function pickStopReason(flags: StopFlags): StopReason {
  if (flags.error) return 'error';
  if (flags.ended) return 'ended';
  if (flags.mute) return 'mute';
  if (flags.button) return flags.paused ? 'pause' : 'stop';
  if (flags.limit) return 'limit';
  if (flags.leave) return 'leave';
  return 'self';
}

/** Остановку никто не заказывал — ученику надо сказать словами. */
export function isUnexpectedStop(reason: StopReason): boolean {
  return reason === 'self' || reason === 'ended' || reason === 'mute' || reason === 'error';
}

/**
 * Свои слова на каждый случай, а не «что-то пошло не так».
 *
 * ⚠️ Общий текст на все причины — то же самое молчание: «микрофон отключился»
 * ведёт человека проверить наушники, «браузер дал сбой» — перезаписать, и это
 * разные действия.
 */
export function stopReasonKey(reason: StopReason): string | null {
  switch (reason) {
    case 'ended':
      return 'blockRenderers.recordStopEnded';
    case 'mute':
      return 'blockRenderers.recordStopMute';
    case 'error':
      return 'blockRenderers.recordStopError';
    case 'self':
      return 'blockRenderers.recordStopSelf';
    default:
      return null;
  }
}
